import { PublishButton } from "./PublishButton";
import { isAttributeEditable } from "../../utils/data/entityPermissions";
import { PanelWidthVariation } from "../panelWidthVariations";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import { DeleteButton } from "../../utils/components/DeleteButton";
import { Issue, PermittedAction } from "@adsk/acc-issues-types";
import { IssueDetailTabActionsDropdown } from "./IssueDetailTabActionsDropdown";
import { PanelSubHeader } from "../../utils/components/Panel";
import React, { useCallback, useContext, useState } from "react";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  trackIssueDeleteCancel,
  trackIssueDeleteClick,
  trackIssueDeleteConfirm,
} from "../../utils/analytics/issueDeletionTracking";
import { ButtonDivider } from "../../utils/components/ButtonDivider";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { RestoreDeletedIssueButton } from "../../utils/components/RestoreDeletedIssueButton";
import { RestoreDeletedIssueConfirmationModal } from "../../utils/components/RestoreDeletedIssueConfirmationModal";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { IssueDeletionCommentField } from "./IssueDeletionCommentField";
import { useIssueDeletionComment } from "./hooks/IssueDeleteHooks/useIssueDeletionComment";
import { useHasPermissionToRestoreIssue } from "./hooks/IssueDeleteHooks/useHasPermissionToRestoreIssues";
import { trackEvent } from "@adsk/acc-issues-core/utils/analytics/trackEvent";
import {
  IssueRestoreButtonCancelAnalyticEvent,
  IssueRestoreButtonClickAnalyticEvent,
  IssueRestoreButtonConfirmAnalyticEvent,
} from "../../utils/analytics/analyticsLib093IssuesEvents";
import { useDeleteIssue } from "../../hooks/useDeleteIssue";

interface IssueDetailTabPanelSubHeaderProps {
  issue: Issue;
  handleChange: (
    inputIssueChanges: any,
    handlers?: Partial<{
      onSuccess: any;
      onFailure: any;
    }>
  ) => void;
  panelRef: React.MutableRefObject<null>;
  widthVariation: PanelWidthVariation;
  onDelete?: (issueId: string) => void;
  onChangePublishStatus?: (issueId: string, isNowPublished: boolean) => void;
  onRestoreSuccess?: () => void;
}

export const IssueDetailTabPanelSubHeader = ({
  issue,
  handleChange,
  panelRef,
  widthVariation,
  onDelete,
  onChangePublishStatus,
  onRestoreSuccess,
}: IssueDetailTabPanelSubHeaderProps) => {
  const { showNotification } = useNotifications();
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [
    shouldWarnAboutReferencesOnDelete,
    setShouldWarnAboutReferencesOnDelete,
  ] = useState(false);
  const { T } = useTranslations();
  const deleteIssueById = useDeleteIssue();
  const isDeletedIssuesSpaceActive = useContext(DeletedIssuesSpaceContext);
  const [isRestoreConfirmationModalOpen, setIsRestoreConfirmationModalOpen] =
    useState(false);

  const hasPermissionToRestoreIssue = useHasPermissionToRestoreIssue(issue);

  const { value: isAbleToRestoreIssues } = useFeatureFlag(
    FeatureFlag.RestoreDeletedIssues
  );

  const {
    issueDeletionComment,
    setIssueDeletionComment,
    isAbleToDeleteWithComment,
    maxCommentLength,
    isCommentTruncated,
  } = useIssueDeletionComment();

  const { value: dontDeleteIssueMarkups } = useFeatureFlag(
    FeatureFlag.DontDeleteIssueMarkups
  );

  let deleteMessage;
  if (isAbleToRestoreIssues) {
    deleteMessage = T.translate(
      "issueEditForm.delete.newModalMessageWithRestore"
    );
  } else if (isAbleToDeleteWithComment) {
    deleteMessage = T.translate("issueEditForm.delete.newModalMessage");
  } else {
    if (shouldWarnAboutReferencesOnDelete) {
      deleteMessage = T.translate("issueEditForm.delete.warnAboutReferences");
    } else {
      deleteMessage = T.translate("issueEditForm.delete.modalMessage");
    }
  }

  const handleDelete = useCallback(() => {
    const onDeleteSuccess = () => {
      onDelete && onDelete(issue.id);
      setIssueDeletionComment("");
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: T.translate("issueEditForm.delete.successMessage"),
        dismissible: true,
      });
    };

    const onDeleteFailure = () =>
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate("issueEditForm.delete.failureMessage"),
        dismissible: true,
      });

    trackIssueDeleteConfirm({
      issueUid: issue.id,
      comment: issueDeletionComment.length > 0,
    });

    deleteIssueById({
      id: issue.id,
      comment: issueDeletionComment,
      onSuccess: onDeleteSuccess,
      onFailure: onDeleteFailure,
      shouldDeleteMarkup: !dontDeleteIssueMarkups,
    });
  }, [
    issue.id,
    issueDeletionComment,
    dontDeleteIssueMarkups,
    onDelete,
    setIssueDeletionComment,
    showNotification,
    T,
    deleteIssueById,
  ]);

  const isPublishButtonDisabled =
    isDeletedIssuesSpaceActive ||
    !isAttributeEditable(issue, "published") ||
    !onChangePublishStatus;

  const restoreDeletedButtonClickHandler = useCallback(() => {
    setIsRestoreConfirmationModalOpen(true);
    trackEvent(new IssueRestoreButtonClickAnalyticEvent());
  }, []);

  const closeRestoreModal = useCallback(() => {
    setIsRestoreConfirmationModalOpen(false);
  }, []);

  const onRestoreModalConfirm = useCallback(() => {
    closeRestoreModal();
    trackEvent(new IssueRestoreButtonConfirmAnalyticEvent());
  }, [closeRestoreModal]);

  const onRestoreModalClose = useCallback(() => {
    closeRestoreModal();
    trackEvent(new IssueRestoreButtonCancelAnalyticEvent());
  }, [closeRestoreModal]);

  return (
    <PanelSubHeader
      style={{ display: "flex", justifyContent: "space-between" }}
    >
      <div style={{ display: "flex" }}>
        <PublishButton
          issue={issue}
          onChangePublishStatus={onChangePublishStatus}
          disabled={isPublishButtonDisabled}
          handleChange={handleChange}
          tooltipContainer={panelRef}
          shouldBeSkinny={widthVariation === PanelWidthVariation.Skinny}
        />
        {!isDeletedIssuesSpaceActive && (
          <>
            <ButtonDivider />
            <DeleteButton
              disabled={
                !issue.permittedActions.includes(PermittedAction.Delete) ||
                !onDelete
              }
              disabledReason={T.translate("issueEditForm.delete.noPermissions")}
              tooltipContainer={panelRef}
              onClick={() => {
                setIsDeleteConfirmationModalOpen(true);
                trackIssueDeleteClick();
                // although checking for the existence of references via the DOM is a somewhat brittle choice,
                // this choice is made because it is clearer and less complected than splitting the reference
                // picker into pieces and making the provider live much higher in the tree, just for the sole
                // purpose of determining if there are references or not.
                // In the future if we need to do more logic related to references
                // in components that aren't the reference picker itself, then we should do that.
                // Until then, this is clearer, simpler, and more expedient.
                const referencePickerEmptyStateNodes =
                  document.querySelectorAll(
                    `.IssueDetailPanel.issue-${issue.id} [class^=ReferencePickerEmptyState]`
                  );
                setShouldWarnAboutReferencesOnDelete(
                  !referencePickerEmptyStateNodes.length
                );
              }}
              shouldBeSkinny={widthVariation === PanelWidthVariation.Skinny}
            />
          </>
        )}
        {isAbleToRestoreIssues && isDeletedIssuesSpaceActive && (
          <RestoreDeletedIssueButton
            disabled={!hasPermissionToRestoreIssue}
            tooltipContainer={panelRef}
            disabledReason={T.translate(
              "deletedIssues.noPermissionToRestoreMessage"
            )}
            shouldBeSkinny={widthVariation === PanelWidthVariation.Skinny}
            onClick={restoreDeletedButtonClickHandler}
          />
        )}
        <RestoreDeletedIssueConfirmationModal
          show={isRestoreConfirmationModalOpen}
          onConfirm={onRestoreModalConfirm}
          onClose={onRestoreModalClose}
          issuesToRestore={[issue]}
          onRestoreSuccess={onRestoreSuccess}
          title={T.translate("deletedIssues.restoreModal.title") as string}
          bodyText={T.translate("deletedIssues.restoreModal.message") as string}
        />
        <ModalConfirmation
          style={{}}
          show={isDeleteConfirmationModalOpen}
          title={T.translate("issueEditForm.delete.modalTitle") as string}
          message={
            <>
              <span>{deleteMessage}</span>
              {isAbleToDeleteWithComment && (
                <IssueDeletionCommentField
                  value={issueDeletionComment}
                  onChange={setIssueDeletionComment}
                  maxCommentLength={maxCommentLength}
                  isCommentTruncated={isCommentTruncated}
                />
              )}
            </>
          }
          onConfirm={async () => {
            setIsDeleteConfirmationModalOpen(false);
            handleDelete();
          }}
          onClose={() => {
            setIsDeleteConfirmationModalOpen(false);
            const hasComment = issueDeletionComment.length > 0;
            trackIssueDeleteCancel({ comment: hasComment });
            setIssueDeletionComment("");
          }}
          confirmationButtonLabel={
            T.translate("issueEditForm.delete.deleteIssue") as string
          }
          data-dd-action-name="<MASKED-DATA>"
        ></ModalConfirmation>
      </div>
      <IssueDetailTabActionsDropdown issue={issue} />
    </PanelSubHeader>
  );
};
